import React from "react";
import { Link } from "react-router-dom";
import { Heart, MapPin, Zap, Flame } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { VerifBadge, Rating, DemoTag, cn } from "@/components/common";
import { PLACEHOLDER_IMG } from "@/lib/api";

const FALLBACK = PLACEHOLDER_IMG;

function isFeatured(o) { return o.featured_until && o.featured_until > new Date().toISOString(); }

export function FeaturedTag() {
  return <span data-testid="featured-tag" className="inline-flex items-center gap-1 rounded-md border border-brand-green/40 bg-brand-green/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-green">Featured</span>;
}

export function BusinessCard({ b }) {
  return (
    <Link to={`/business/${b.id}`} data-testid={`business-card-${b.id}`}
      className="card-hover group block overflow-hidden rounded-2xl border border-border bg-card">
      <div className="relative h-32 w-full overflow-hidden bg-muted">
        <img src={fileUrl(b.cover_url) || FALLBACK} alt={b.name} loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute left-2 top-2 flex gap-1.5">
          {isFeatured(b) && <span className="rounded-md bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">FEATURED</span>}
          {b.is_demo && <DemoTag />}
        </div>
      </div>
      <div className="space-y-1.5 p-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-1 font-display text-[15px] font-semibold">{b.name}</h3>
        </div>
        <p className="text-xs font-medium text-primary">{b.category}</p>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3" /> {b.suburb ? `${b.suburb}, ` : ""}{b.state}
        </div>
        <div className="flex items-center justify-between pt-1">
          <Rating value={b.rating} count={b.review_count} />
          {b.verified && <VerifBadge type="verified" />}
        </div>
      </div>
    </Link>
  );
}

export function ListingCard({ l, onUnsave }) {
  const unsave = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (onUnsave) onUnsave(l.id);
  };
  return (
    <Link to={`/listing/${l.id}`} data-testid={`listing-card-${l.id}`}
      className="card-hover group block overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        <img src={fileUrl(l.photos?.[0]) || FALLBACK} alt={l.title} loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute left-2 top-2 flex gap-1.5">
          {isFeatured(l) && <FeaturedTag />}
          {l.urgent && <span className="inline-flex items-center gap-0.5 rounded-md bg-accent px-1.5 py-0.5 text-[10px] font-bold text-white"><Zap className="h-2.5 w-2.5" />URGENT</span>}
          {l.boosted_until && l.boosted_until > new Date().toISOString() && <span className="inline-flex items-center gap-0.5 rounded-md bg-primary px-1.5 py-0.5 text-[10px] font-bold text-white"><Flame className="h-2.5 w-2.5" />BOOST</span>}
          {l.is_demo && <DemoTag />}
        </div>
        {onUnsave && (
          <button onClick={unsave} data-testid={`unsave-${l.id}`} aria-label="Remove from saved"
            className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-md transition-colors hover:bg-black/70">
            <Heart className="h-4 w-4 fill-accent text-accent" />
          </button>
        )}
        <div className="absolute bottom-2 left-2 rounded-lg bg-brand-green px-2.5 py-1 text-sm font-bold text-white shadow-md backdrop-blur-md">
          {l.price > 0 ? `$${l.price.toLocaleString()}` : "Free"}
          {l.negotiable && <span className="ml-1 text-[10px] font-medium text-white/80">ONO</span>}
        </div>
      </div>
      <div className="space-y-1 p-3.5">
        <h3 className="line-clamp-1 text-sm font-semibold text-foreground">{l.title}</h3>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3" /> {l.suburb ? `${l.suburb}, ` : ""}{l.state} · {l.condition}
        </div>
      </div>
    </Link>
  );
}

export function ServiceCard({ s }) {
  return (
    <Link to={`/service/${s.id}`} data-testid={`service-card-${s.id}`}
      className="card-hover block rounded-2xl border border-border bg-card p-4">
      <p className="text-xs font-semibold text-primary">{s.category}</p>
      <h3 className="mt-1 line-clamp-1 font-display text-[15px] font-semibold">{s.name}</h3>
      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{s.description}</p>
      <div className="mt-2 flex items-center justify-between">
        <span className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" />{s.state}</span>
        <Rating value={s.rating} count={s.review_count} />
      </div>
    </Link>
  );
}

export function RequestCard({ r }) {
  return (
    <Link to={`/request/${r.id}`} data-testid={`request-card-${r.id}`}
      className="card-hover block rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="rounded-full bg-accent/10 px-2 py-0.5 text-[11px] font-semibold text-accent">{r.category}</span>
        <span className="text-[11px] text-muted-foreground">{r.response_count} responses</span>
      </div>
      <h3 className="mt-2 line-clamp-2 font-display text-[15px] font-semibold">{r.title}</h3>
      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        <MapPin className="h-3 w-3" />{r.suburb ? `${r.suburb}, ` : ""}{r.state}
        {r.budget ? <span className="font-semibold text-primary">· Budget ${r.budget}</span> : null}
      </div>
    </Link>
  );
}

export { cn };
